import type DateRange from '../../lib/dateRange';
import { CalculationTypes } from '../../lib/constants';
import { type Measure, type MetricSeriesResult } from '../../types';
import { isStoryMeasuresBatchQueryEnabled } from 'common/visualizations/helpers/VifSelectors';

import { calculateBatchedAverageMeasures } from './average';
import { calculateBatchedSumMeasures } from './sum';
import { calculateBatchedCountMeasures } from './count';
import { calculateBatchedRateMeasures } from './rate';
import { calculateBatchedRecentMeasures } from './recent_value';

/**
 * Main entry point for batch calculations.
 * Automatically dispatches to the appropriate batch calculation function based on measure type.
 */
export const calculateBatchedMeasures = async (
  measure: Measure,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  const calculationType = measure?.metricConfig?.type;

  switch (calculationType) {
    case CalculationTypes.AVERAGE:
      return calculateBatchedAverageMeasures(measure, reportingPeriods);

    case CalculationTypes.SUM:
      return calculateBatchedSumMeasures(measure, reportingPeriods);

    case CalculationTypes.COUNT:
      return calculateBatchedCountMeasures(measure, reportingPeriods);

    case CalculationTypes.RATE:
      return calculateBatchedRateMeasures(measure, reportingPeriods);

    case CalculationTypes.RECENT:
      return calculateBatchedRecentMeasures(measure, reportingPeriods);

    default:
      throw new Error(`Unsupported calculation type for batch processing: ${calculationType}`);
  }
};

/**
 * Determines if batch optimization should be used for the given measure and conditions.
 */
export const shouldUseBatchOptimization = (
  measure: Measure | null,
  reportingPeriods: DateRange[],
  lastPeriodOnly: boolean,
): boolean => {
  if (!isStoryMeasuresBatchQueryEnabled() || !measure || lastPeriodOnly) {
    return false;
  }

  const calculationType = measure?.metricConfig?.type;

  // Only enable batch for calculation types that support it
  const supportedTypes = [
    CalculationTypes.AVERAGE,
    CalculationTypes.SUM,
    CalculationTypes.COUNT,
    CalculationTypes.RATE,
    CalculationTypes.RECENT,
  ];

  if (!supportedTypes.includes(calculationType)) {
    return false;
  }

  // Only use batch for multiple periods (single period has no benefit)
  if (reportingPeriods.length <= 1) {
    return false;
  }

  return true;
};
